import { useEffect, useState, useCallback, useRef } from "react";
import { apiFetch } from "../context/AuthContext";

const B    = "#2C1A0E";
const ROSE = "#C9847A";
const fmtR = (n) => n ? `₹${Number(n).toLocaleString("en-IN")}` : "—";

const CAT_CONFIG = {
  opex:      { label: "OpEx",      color: "#1D4ED8", bg: "#DBEAFE" },
  cogs:      { label: "COGS",      color: "#15803D", bg: "#DCFCE7" },
  marketing: { label: "Marketing", color: "#7C3AED", bg: "#EDE9FE" },
  admin:     { label: "Admin",     color: "#92400E", bg: "#FEF9C3" },
};

const MONTHS_ALL = [
  "Apr-25","May-25","Jun-25","Jul-25","Aug-25","Sep-25",
  "Oct-25","Nov-25","Dec-25","Jan-26","Feb-26","Mar-26",
  "Apr-26","May-26","Jun-26","Jul-26","Aug-26","Sep-26",
  "Oct-26","Nov-26","Dec-26","Jan-27","Feb-27","Mar-27",
];

export default function CapExPage() {
  const [rows,      setRows]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState("");
  const [catFilter, setCatFilter] = useState([]);
  const [view,      setView]      = useState("summary"); // summary | detail
  const [dateFrom,  setDateFrom]  = useState("Apr-26");
  const [dateTo,    setDateTo]    = useState("Mar-27");
  const searchTimer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    const categories = catFilter.length > 0 ? catFilter : ["opex","cogs","marketing","admin"];
    const params = new URLSearchParams();
    categories.forEach(c => params.append("category", c));
    if (search) params.set("search", search);
    const r = await apiFetch(`/api/expenses?${params}`);
    if (r.ok) {
      const d = await r.json();
      setRows(d.rows || []);
    }
    setLoading(false);
  }, [search, catFilter]);

  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(load, 300);
  }, [search, catFilter]);

  // Filter by month range
  const fromIdx = MONTHS_ALL.indexOf(dateFrom);
  const toIdx   = MONTHS_ALL.indexOf(dateTo);
  const filtered = rows.filter(r => {
    const mi = MONTHS_ALL.indexOf(r.month);
    return mi >= fromIdx && mi <= toIdx;
  });

  // Aggregate by line_item for summary
  const byLine = {};
  filtered.forEach(r => {
    if (!byLine[r.line_item]) byLine[r.line_item] = { line_item: r.line_item, category: r.category, plan: 0, actual: 0 };
    byLine[r.line_item].plan   += r.plan_amount   || 0;
    byLine[r.line_item].actual += r.actual_amount || 0;
  });

  // Aggregate by category
  const byCat = {};
  Object.values(byLine).forEach(l => {
    if (!byCat[l.category]) byCat[l.category] = { plan: 0, actual: 0 };
    byCat[l.category].plan   += l.plan;
    byCat[l.category].actual += l.actual;
  });

  const totalPlan   = Object.values(byCat).reduce((s, c) => s + c.plan, 0);
  const totalActual = Object.values(byCat).reduce((s, c) => s + c.actual, 0);

  const toggleCat = (c) => setCatFilter(prev =>
    prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]
  );

  return (
    <div style={{ padding: "28px 28px 80px", fontFamily: "Inter, sans-serif" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <h1 style={{ fontFamily: "'Cormorant Garamond',Georgia,serif",
                       color: B, fontSize: 26, fontWeight: 700, margin: 0 }}>
            🔧 CapEx / OpEx
          </h1>
          <p style={{ color: "#888", fontSize: 13, marginTop: 4 }}>
            Expenses from Zoho Books (auto-categorised) · Plan vs Actual
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={sel}>
            {MONTHS_ALL.map(m => <option key={m}>{m}</option>)}
          </select>
          <span style={{ color: "#aaa" }}>→</span>
          <select value={dateTo} onChange={e => setDateTo(e.target.value)} style={sel}>
            {MONTHS_ALL.map(m => <option key={m}>{m}</option>)}
          </select>
        </div>
      </div>

      {/* KPI strip */}
      <div style={{ display: "flex", gap: 14, marginBottom: 20, flexWrap: "wrap" }}>
        {[
          { label: "Total Plan",    value: fmtR(totalPlan) },
          { label: "Total Actual",  value: fmtR(totalActual),
            color: totalActual > totalPlan ? "#DC2626" : "#15803D" },
          { label: "vs Budget",     value: totalActual <= totalPlan
              ? `▼ ${fmtR(totalPlan - totalActual)} under`
              : `▲ ${fmtR(totalActual - totalPlan)} over`,
            color: totalActual <= totalPlan ? "#15803D" : "#DC2626" },
          ...Object.entries(byCat).map(([cat, v]) => ({
            label: CAT_CONFIG[cat]?.label || cat,
            value: fmtR(v.actual),
            color: CAT_CONFIG[cat]?.color,
          })),
        ].map(({ label, value, color }) => (
          <div key={label} style={kpiCard}>
            <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: color || B }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search line item…"
          style={{ ...inp, flex: 1, minWidth: 200 }}
        />
        <div style={{ display: "flex", gap: 6 }}>
          {Object.entries(CAT_CONFIG).map(([cat, cfg]) => (
            <button key={cat} onClick={() => toggleCat(cat)} style={{
              background: (catFilter.length === 0 || catFilter.includes(cat)) ? cfg.bg : "#F0EBE3",
              color: (catFilter.length === 0 || catFilter.includes(cat)) ? cfg.color : "#aaa",
              border: `1px solid ${catFilter.includes(cat) ? cfg.color : "#DDD4C8"}`,
              borderRadius: 6, padding: "5px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer",
            }}>{cfg.label}</button>
          ))}
        </div>
        {/* View toggle */}
        <div style={{ display: "flex", gap: 0, border: "1px solid #DDD4C8", borderRadius: 7, overflow: "hidden" }}>
          {[["summary","Summary"],["detail","Monthly Detail"]].map(([id, label]) => (
            <button key={id} onClick={() => setView(id)} style={{
              background: view === id ? B : "#FAF7F2", color: view === id ? "#FAF7F2" : "#888",
              border: "none", padding: "6px 14px", fontSize: 12, cursor: "pointer",
            }}>{label}</button>
          ))}
        </div>
      </div>

      {loading && <div style={{ color: "#aaa", fontSize: 13 }}>Loading…</div>}

      {/* ── Summary view: line items ── */}
      {!loading && view === "summary" && (
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13,
                        background:"#fff", border:"1px solid #EDE0D4", borderRadius:12, overflow:"hidden" }}>
          <thead>
            <tr style={{ background: "#F7F5F2" }}>
              <th style={th}>Category</th>
              <th style={th}>Line Item</th>
              <th style={{ ...th, textAlign: "right" }}>Plan</th>
              <th style={{ ...th, textAlign: "right" }}>Actual</th>
              <th style={{ ...th, textAlign: "right" }}>vs Budget</th>
              <th style={th}>Usage</th>
            </tr>
          </thead>
          <tbody>
            {Object.values(byLine)
              .sort((a,b) => (b.actual || b.plan) - (a.actual || a.plan))
              .map(l => {
                const cfg  = CAT_CONFIG[l.category] || {};
                const diff = l.actual - l.plan;
                const pct  = l.plan > 0 ? Math.round((l.actual / l.plan) * 100) : null;
                return (
                  <tr key={l.line_item} style={{ borderBottom: "1px solid #F0E8E0" }}>
                    <td style={td}>
                      <span style={{ background: cfg.bg, color: cfg.color, borderRadius: 4,
                                     padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>
                        {cfg.label || l.category}
                      </span>
                    </td>
                    <td style={{ ...td, fontWeight: 500, color: B }}>{l.line_item}</td>
                    <td style={{ ...td, textAlign: "right", color: "#555" }}>{fmtR(l.plan)}</td>
                    <td style={{ ...td, textAlign: "right", fontWeight: 600,
                                 color: diff > 0 ? "#DC2626" : l.actual > 0 ? "#15803D" : "#aaa" }}>
                      {fmtR(l.actual)}
                    </td>
                    <td style={{ ...td, textAlign: "right", fontSize: 12,
                                 color: diff > 0 ? "#DC2626" : "#15803D" }}>
                      {diff > 0 ? `+${fmtR(diff)}` : diff < 0 ? `-${fmtR(-diff)}` : "—"}
                    </td>
                    <td style={{ ...td, minWidth: 120 }}>
                      {pct != null && (
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{ flex: 1, background: "#F0EBE3", borderRadius: 3, height: 6 }}>
                            <div style={{ width: `${Math.min(pct, 100)}%`, height: "100%", borderRadius: 3,
                                          background: pct > 110 ? "#DC2626" : pct > 90 ? "#F59E0B" : "#15803D" }} />
                          </div>
                          <span style={{ fontSize: 11, color: "#888", minWidth: 32 }}>{pct}%</span>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      )}

      {/* ── Monthly detail view ── */}
      {!loading && view === "detail" && (() => {
        const inRange = MONTHS_ALL.slice(fromIdx, toIdx + 1);
        const lines   = [...new Set(filtered.map(r => r.line_item))];
        const lookup  = {};
        filtered.forEach(r => { lookup[`${r.line_item}__${r.month}`] = r; });
        return (
          <div style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", fontSize: 12,
                            background: "#fff", border: "1px solid #EDE0D4", borderRadius: 12, minWidth: 800 }}>
              <thead>
                <tr style={{ background: "#F7F5F2" }}>
                  <th style={{ ...th, position: "sticky", left: 0, background: "#F7F5F2", minWidth: 180 }}>
                    Line Item
                  </th>
                  {inRange.map(m => (
                    <th key={m} style={{ ...th, textAlign: "right", minWidth: 80 }}>{m}</th>
                  ))}
                  <th style={{ ...th, textAlign: "right", minWidth: 90 }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {lines.map(line => {
                  const lineRows = inRange.map(m => lookup[`${line}__${m}`]);
                  const total    = lineRows.reduce((s, r) => s + (r?.actual_amount || 0), 0);
                  const cat      = lineRows.find(r => r)?.category;
                  const cfg      = CAT_CONFIG[cat] || {};
                  return (
                    <tr key={line} style={{ borderBottom: "1px solid #F0E8E0" }}>
                      <td style={{ ...td, position: "sticky", left: 0, background: "#fff",
                                   fontWeight: 500, color: B }}>
                        <span style={{ display:"inline-block", width:8, height:8, borderRadius:2,
                                       background: cfg.color || "#ccc", marginRight:8 }}/>
                        {line}
                      </td>
                      {inRange.map(m => {
                        const row = lookup[`${line}__${m}`];
                        const act = row?.actual_amount || 0;
                        return (
                          <td key={m} style={{ ...td, textAlign: "right",
                                               color: act > 0 ? "#333" : "#ddd" }}>
                            {act > 0 ? fmtR(act) : "—"}
                          </td>
                        );
                      })}
                      <td style={{ ...td, textAlign: "right", fontWeight: 700, color: B }}>
                        {fmtR(total)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })()}
    </div>
  );
}

const kpiCard = { background:"#fff", border:"1px solid #EDE0D4", borderRadius:12,
                  padding:"12px 16px", flex:1, minWidth:120 };
const inp = { border:"1px solid #DDD4C8", borderRadius:7, padding:"7px 12px",
              fontSize:13, background:"#FAF7F2", color:"#2C1A0E" };
const sel = { ...inp, minWidth: 100 };
const th  = { padding:"9px 14px", textAlign:"left", fontWeight:600, color:"#666",
              fontSize:11, textTransform:"uppercase", letterSpacing:0.5 };
const td  = { padding:"10px 14px", verticalAlign:"middle" };
